import PropTypes from 'prop-types';
import React, { Component } from 'react';
import autoBind from 'react-autobind';

import { fetchRate, fetchQuotes } from './clients';
import iso3ToIso2 from './country-mapper';
import createUrl from './createUrl';
import makeAsync from './makeAsync';
import {
  trackAmountChange,
  trackClickToSendMoney,
  trackRouteChange,
  trackWhenError,
  trackIfWiseIsNotTheCheapest,
  trackClickToSeePricingDisclaimer,
  trackQuotesLoad,
  trackSpeedTooltipClick,
  trackIfWiseIsNotTheFastest,
  trackClickToSeeDisclaimer,
  trackProviderClicked,
} from './tracking';

const executeAsync = (fn) => makeAsync(fn)();

function parseBool(input) {
  if (typeof input === 'boolean') {
    return input;
  }
  return input === 'true';
}

function toLowerCase(providerNames) {
  return providerNames.map((p) => p.toLowerCase());
}

function normaliseToIso2(country) {
  return !country || country.length === 2 ? country : iso3ToIso2(country);
}

function extractRequiredProps(props) {
  const sendAmount = props.sendAmount ? parseFloat(props.sendAmount) : parseFloat(props.amount);
  const sourceCurrency = props.sourceCurrency || props.source;
  const targetCurrency = props.targetCurrency || props.target;
  return { sendAmount, sourceCurrency, targetCurrency };
}

function isValidAffiliate(affiliateLink) {
  return !!(
    affiliateLink.match(/^(https?:\/\/transferwise\.com)\//) ||
    affiliateLink.match(/^(https?:\/\/wise\.com)\//) ||
    affiliateLink.match(/^(https?:\/\/(wise\.|transferwise\.)?prf\.hn)\//)
  );
}

function withComparisonResources(WrappedComponent) {
  class RequestComparisonResources extends Component {
    constructor(props) {
      super(props);
      const { route, quotes, wiseFee } = this.props;
      autoBind(this);

      this.componentType = props.componentType
        ? props.componentType
        : WrappedComponent.componentType;

      const { sendAmount, sourceCurrency, targetCurrency } = extractRequiredProps(props);

      this.state = {
        sendAmount,
        lastAmountFetched: sendAmount,
        sourceCurrency,
        targetCurrency,

        midMarketRate: route?.rate,
        wiseFee,

        // TODO Rename
        route,
        quotes,

        error: false,

        loadingRates: !route,
        loadingQuotes: quotes.length === 0,

        // State private to this component
        sourceCountry: normaliseToIso2(props.sourceCountry),
        providerCountry: props.providerCountry,
        whitelistedProviders: props.providers ? toLowerCase(props.providers) : null,
      };
    }

    componentDidMount() {
      const { sendAmount, sourceCurrency, targetCurrency } = extractRequiredProps(this.props);

      const { sourceCountry, providerCountry, wiseFee, useV3ComparisonApi, providers } = {
        ...this.props,
        ...this.state,
      };

      const { route, quotes, trackEvent } = { ...this.props };
      if (!route) {
        this.fetchAndProcessRates(sourceCurrency, targetCurrency);
      }
      if (quotes.length === 0) {
        this.fetchAndProcessQuotes(
          sourceCurrency,
          targetCurrency,
          sendAmount,
          sourceCountry,
          providerCountry,
          route?.rate,
          wiseFee,
          useV3ComparisonApi,
          providers,
        ).then((newQuotes) => {
          this.performPostMountTracking(newQuotes, trackEvent);
        });
      } else {
        this.performPostMountTracking(quotes, trackEvent);
      }
    }

    performPostMountTracking(quotes, trackEvent) {
      const context = { ...this.state, componentType: this.componentType };
      const filteredProviders = this.filterByWhitelistedProviders(quotes);
      const enrichedContext = this.enrichContext(context);

      trackQuotesLoad(context, quotes, trackEvent);
      trackIfWiseIsNotTheCheapest(filteredProviders, enrichedContext, trackEvent);
      trackIfWiseIsNotTheFastest(filteredProviders, enrichedContext, trackEvent);
    }

    onRouteChange(route, context) {
      const { sourceCurrency, targetCurrency } = this.state;
      const derivedSourceCurrency = route.sourceCurrency || sourceCurrency;
      const derivedTargetCurrency = route.targetCurrency || targetCurrency;

      this.setState(
        {
          sourceCurrency: derivedSourceCurrency,
          targetCurrency: derivedTargetCurrency,
          midMarketRate: this.isOriginalRoute(derivedSourceCurrency, derivedTargetCurrency)
            ? this.props.route?.rate
            : null,
        },
        () => this.continueOnRouteChange(route, context),
      );
    }

    continueOnRouteChange(route, context) {
      const { onComparisonParamsChange, trackEvent, useV3ComparisonApi, providers } = this.props;
      const { sendAmount, sourceCurrency, targetCurrency, midMarketRate, wiseFee, sourceCountry } =
        this.state;

      const updatedFilters = this.removeAllFilters();

      executeAsync(() =>
        onComparisonParamsChange({
          sourceCurrency,
          targetCurrency,
          sendAmount,
        }),
      );

      return this.fetchAndProcessAllResources(
        sourceCurrency,
        targetCurrency,
        sendAmount,
        sourceCountry,
        updatedFilters.providerCountry,
        midMarketRate,
        wiseFee,
        useV3ComparisonApi,
        providers,
      ).then((response) => {
        const updatedContext = { ...context, quotes: response.quotes };

        trackIfWiseIsNotTheCheapest(
          this.filterByWhitelistedProviders(response.quotes),
          this.enrichContext(context),
          trackEvent,
        );
        trackIfWiseIsNotTheFastest(
          this.filterByWhitelistedProviders(response.quotes),
          this.enrichContext(context),
          trackEvent,
        );
        trackRouteChange(route, this.enrichContext(updatedContext), trackEvent);
      });
    }

    onError(errorType, context) {
      const { trackEvent } = this.props;
      trackWhenError(errorType, this.enrichContext(context), trackEvent);
    }

    onCallToActionClick(context) {
      const { trackLeavePageEvent } = this.props;
      makeAsync(trackLeavePageEvent);

      const navigateTo = () => {
        window.location.href = this.linkToTW(context);
      };

      trackClickToSendMoney(this.enrichContext(context), trackLeavePageEvent, navigateTo);
    }

    onClickToSeePricingDisclaimer(targetCountry, providerAlias, context) {
      const { trackEvent } = this.props;
      trackClickToSeePricingDisclaimer(
        targetCountry,
        providerAlias,
        this.enrichContext(context),
        trackEvent,
      );
    }

    onClickingDeliveryEstimationTooltip(context) {
      const { trackEvent } = this.props;
      trackSpeedTooltipClick(this.enrichContext(context), trackEvent);
    }

    onClickToSeeDisclaimer(context) {
      const { trackEvent } = this.props;
      trackClickToSeeDisclaimer(this.enrichContext(context), trackEvent);
    }

    onProviderClick(providerName, context) {
      const { trackEvent } = this.props;
      trackProviderClicked(providerName, this.enrichContext(context), trackEvent);
    }

    onAmountChangeFinish(amount, context) {
      const { trackEvent } = this.props;
      const { lastAmountFetched } = this.state;
      this.fetchQuotesForAmount(amount).then((response) => {
        this.setState({ lastAmountFetched: amount });

        const updatedContext = { ...context, quotes: response.quotes };
        trackAmountChange(
          amount,
          lastAmountFetched,
          this.enrichContext(updatedContext),
          trackEvent,
        );
      });
    }

    updateAmountAndShowLoader(inputValue) {
      const { onComparisonParamsChange } = this.props;
      const { sourceCurrency, targetCurrency } = this.state;
      this.setState({ loadingQuotes: true, sendAmount: inputValue });

      executeAsync(() => {
        onComparisonParamsChange({
          sourceCurrency,
          targetCurrency,
          sendAmount: inputValue,
        });
      });
    }

    fetchQuotesForAmount(amount) {
      const { sourceCurrency, targetCurrency, sourceCountry, providerCountry, midMarketRate } =
        this.state;
      const { useV3ComparisonApi, wiseFee, providers } = this.props;
      return this.fetchAndProcessQuotes(
        sourceCurrency,
        targetCurrency,
        amount,
        sourceCountry,
        providerCountry,
        midMarketRate,
        wiseFee,
        useV3ComparisonApi,
        providers,
      ).then((response) => {
        this.setState({ lastAmountFetched: amount });
        return response;
      });
    }

    fetchAndProcessAllResources(
      sourceCurrency,
      targetCurrency,
      sendAmount,
      sourceCountry,
      providerCountry,
      midMarketRate,
      wiseFee,
      useV3ComparisonApi,
      providers,
    ) {
      const ratesPromise = this.fetchAndProcessRates(sourceCurrency, targetCurrency);
      const quotesPromise = this.fetchAndProcessQuotes(
        sourceCurrency,
        targetCurrency,
        sendAmount,
        sourceCountry,
        providerCountry,
        midMarketRate,
        wiseFee,
        useV3ComparisonApi,
        providers,
      );

      return Promise.all([ratesPromise, quotesPromise]).then((response) => ({
        route: response[0],
        quotes: response[1],
      }));
    }

    fetchAndProcessRates(sourceCurrency, targetCurrency) {
      return this.loadRates(sourceCurrency, targetCurrency)
        .then((route) => {
          this.setState({ route, loadingRates: false });
          return route;
        })
        .catch(() => {
          this.setState({ loadingRates: false });
          return null;
        });
    }

    fetchAndProcessQuotes(
      sourceCurrency,
      targetCurrency,
      sendAmount,
      sourceCountry,
      providerCountry,
      midMarketRate,
      wiseFee,
      useV3ComparisonApi,
      providers,
    ) {
      return this.loadQuotes(
        sourceCurrency,
        targetCurrency,
        sendAmount,
        sourceCountry,
        providerCountry,
        midMarketRate,
        wiseFee,
        useV3ComparisonApi,
      )
        .then((quotes) => this.filterByWhitelistedProviders(quotes))
        .then((filteredQuotes) => this.validateQuotes(filteredQuotes))
        .then((filteredQuotes) => this.filterByProvidedProviders(filteredQuotes, providers))
        .then((validatedQuotes) => {
          this.setState({ ...validatedQuotes, loadingQuotes: false });
          return validatedQuotes.quotes;
        });
    }

    isOriginalRoute(currentSourceCurrency, currentTargetCurrency) {
      return (
        this.props.sourceCurrency === currentSourceCurrency &&
        this.props.targetCurrency === currentTargetCurrency
      );
    }

    validateQuotes(quotes) {
      if (quotes && !quotes.length) {
        return { quotes, errorType: 'UNSUPPORTED_ROUTE', error: true };
      }
      return { quotes, errorType: null, error: false };
    }

    loadRates(sourceCurrency, targetCurrency) {
      this.setState({ loadingRates: true });
      return fetchRate(sourceCurrency, targetCurrency)
        .then((result) => result.route)
        .catch((error) => {
          this.setState({ loadingRates: false });
          // eslint-disable-next-line no-console
          console.error('Failed to fetch mid-market rate', error);
          throw error;
        });
    }

    loadQuotes(
      sourceCurrency,
      targetCurrency,
      sendAmount,
      sourceCountry,
      providerCountry,
      midMarketRate,
      wiseFee,
      useV3ComparisonApi = false,
    ) {
      this.setState({ loadingQuotes: true });
      return fetchQuotes(sourceCurrency, targetCurrency, sendAmount, midMarketRate, wiseFee, {
        sourceCountry,
        providerCountry,
        filter: 'POPULAR',
        includeWise: true,
        numberOfProviders: 8,
        useV3ComparisonApi,
      }).then((result) => result.quotes);
    }

    filterByWhitelistedProviders(quotes) {
      const { whitelistedProviders } = this.state;
      if (!whitelistedProviders) {
        return quotes;
      }
      return quotes.filter((p) => whitelistedProviders.includes(p.alias));
    }

    linkToTW(context) {
      const { sendAmount, sourceCurrency, targetCurrency } = extractRequiredProps(context);
      const { affiliateLink } = this.props;

      if (affiliateLink && isValidAffiliate(affiliateLink)) {
        return affiliateLink;
      }

      const redirectUrl =
        '/transferFlow#/enterpayment?' +
        `amount=${sendAmount}&` +
        `sourceCurrency=${sourceCurrency}&` +
        `targetCurrency=${targetCurrency}`;

      // TODO: Wise migration
      return createUrl(
        '/register?interested_in=send_money&context=comparison&cta=comparison_send_money' +
          `&redirect_url=${encodeURIComponent(redirectUrl)}#/?redirectUrl=${encodeURIComponent(
            redirectUrl,
          )}`,
      );
    }

    removeAllFilters() {
      const updatedFilters = {
        providerCountry: null,
        whitelistedProviders: null,
      };
      this.setState(updatedFilters);
      return updatedFilters;
    }

    enrichContext(context) {
      const {
        sendAmount,
        sourceCurrency,
        targetCurrency,
        sourceCountry,
        providerCountry,
        whitelistedProviders,
      } = this.state;

      return {
        amount: sendAmount,
        source: sourceCurrency,
        target: targetCurrency,
        sourceCountry,
        providerCountry,
        whitelistedProviders,
        componentType: this.componentType,
        ...context,
      };
    }

    render() {
      // TODO We should probably conditionally render the below once the data is actually available
      const {
        maxVisibleProviders,
        expandDisclaimer,
        excludePartners,
        deliveryEstimationEnabled,
        showSendMoneyButton,
        providerLogoRedirectEnabled,
        trackEvent,
        showMoreProvidersOption,
        page,
        useV3ComparisonApi,
      } = this.props;
      const {
        sendAmount,
        sourceCountry,
        providerCountry,
        quotes,
        error,
        errorType,
        sourceCurrency,
        targetCurrency,
        route,
        loadingQuotes,
        loadingRates,
      } = this.state;

      return (
        <WrappedComponent
          maxVisibleProviders={parseFloat(maxVisibleProviders)}
          expandDisclaimer={parseBool(expandDisclaimer)}
          amount={parseFloat(sendAmount)}
          sourceCountry={sourceCountry}
          providerCountry={providerCountry}
          quotes={quotes}
          error={error}
          errorType={errorType}
          sourceCurrency={sourceCurrency}
          targetCurrency={targetCurrency}
          midMarketRate={route}
          excludePartners={excludePartners}
          loadingQuotes={loadingQuotes}
          loadingRates={loadingRates}
          onAmountChange={this.updateAmountAndShowLoader}
          onAmountChangeFinish={this.onAmountChangeFinish}
          onRouteChange={this.onRouteChange}
          onError={this.onError}
          onCallToActionClick={this.onCallToActionClick}
          onClickToSeePricingDisclaimer={this.onClickToSeePricingDisclaimer}
          onClickingDeliveryEstimationTooltip={this.onClickingDeliveryEstimationTooltip}
          onClickToSeeDisclaimer={this.onClickToSeeDisclaimer}
          deliveryEstimationEnabled={deliveryEstimationEnabled}
          showSendMoneyButton={showSendMoneyButton}
          providerLogoRedirectEnabled={providerLogoRedirectEnabled}
          onProviderClick={this.onProviderClick}
          trackEvent={trackEvent}
          showMoreProvidersOption={showMoreProvidersOption}
          page={page}
          useV3ComparisonApi={useV3ComparisonApi}
        />
      );
    }

    filterByProvidedProviders(filteredQuotes, providers) {
      return providers && providers.length > 0
        ? {
            ...filteredQuotes,
            quotes: filteredQuotes.quotes.filter((q) => providers.includes(q.alias)),
          }
        : filteredQuotes;
    }
  }

  /* eslint-disable react/no-unused-prop-types */
  RequestComparisonResources.propTypes = {
    // We parse this from a string today, and don't expect a number - review
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sendAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    source: PropTypes.string,
    target: PropTypes.string,
    sourceCurrency: PropTypes.string.isRequired,
    targetCurrency: PropTypes.string.isRequired,
    wiseFee: PropTypes.number,

    sourceCountry: PropTypes.string,
    providerCountry: PropTypes.string,

    providers: PropTypes.arrayOf(PropTypes.string),
    maxVisibleProviders: PropTypes.oneOfType([
      // we pass string not number  review
      PropTypes.string,
      PropTypes.number,
    ]),
    expandDisclaimer: PropTypes.oneOfType([
      // we pass string not bool, review
      PropTypes.string,
      PropTypes.bool,
    ]),

    componentType: PropTypes.string,

    // TODO this needs to be renamed, its not really a route
    route: PropTypes.shape({
      rate: PropTypes.number.isRequired,
      source: PropTypes.string.isRequired,
      target: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
    }).isRequired,

    quotes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        alias: PropTypes.string.isRequired,
        logo: PropTypes.string.isRequired,
        rate: PropTypes.number.isRequired,
        fees: PropTypes.number.isRequired,
        receivedAmount: PropTypes.number.isRequired,
        dateCollected: PropTypes.string.isRequired,
      }),
    ),

    excludePartners: PropTypes.bool,
    affiliateLink: PropTypes.string,
    trackEvent: PropTypes.func,
    trackLeavePageEvent: PropTypes.func,
    onComparisonParamsChange: PropTypes.func,
    deliveryEstimationEnabled: PropTypes.bool,
    showSendMoneyButton: PropTypes.bool,
    providerLogoRedirectEnabled: PropTypes.bool,
    showMoreProvidersOption: PropTypes.bool,
    page: PropTypes.string,
    useV3ComparisonApi: PropTypes.bool,
  };
  /* eslint-enable react/no-unused-prop-types */

  RequestComparisonResources.defaultProps = {
    maxVisibleProviders: 5,
    expandDisclaimer: false,
    quotes: [],
    providers: null,

    excludePartners: false,
    affiliateLink: null,
    trackEvent: () => {},
    trackLeavePageEvent: (event, attributes, callback) => callback(),
    onComparisonParamsChange: () => {},
    deliveryEstimationEnabled: false,
    showSendMoneyButton: true,
    sourceCountry: null,
    providerCountry: null,
    componentType: '',
    source: null,
    target: null,
    providerLogoRedirectEnabled: false,
    showMoreProvidersOption: true,
    page: null,
    useV3ComparisonApi: false,
  };

  return RequestComparisonResources;
}

export default withComparisonResources;
